import { create } from "zustand";
import { addApp, addClient, removeApp, removeClient, retrieveApps, updateApp, updateClient, uploadIcon } from "./actions";
import { AppData, RoleData, ClientData } from "./data";
import { addRole, removeRole, retrieveRoles } from "./actionsRole";

type UseAppsProps = {
    loading: boolean
    loadingRoles: boolean
    apps: AppData[] | null
    currentRoles: RoleData[] | null
    retrieveApps: () => Promise<string>
    addApp: (id: string, name: string, desc: string) => Promise<string | undefined>
    addRole: (app: string, role: RoleData) => Promise<string>
    removeRole: (role: string) => Promise<string>
    updateRole: (role: RoleData) => Promise<string>
    retrieveRoles: (app: string) => Promise<string>
    removeApp: (appid: string) => Promise<string | undefined>
    removeClient: (app: string, clientid: string) => Promise<string>
    updateApp: (app: AppData) => Promise<string>
    addClient: (app: string, clientData: Partial<ClientData>) => Promise<any>
    updateClient: (clientData: ClientData) => Promise<any>
    uploadIcon: (img: File, small: boolean, app: string) => Promise<string>
}

export const useApps = create<UseAppsProps>((set, get) => ({
    loading: false,
    loadingRoles: false,
    apps: null,
    currentRoles: [{ id: "roleid", name: "Role", desc: "description" }],
    async retrieveApps() {
        if (get().loading) return ""

        set(state => ({ loading: true }))
        const res = await retrieveApps()
        let apps = res.Msg.apps
        if (!apps) apps = []
        apps.forEach((item: AppData) => {
            const cl: [] = res.Msg.clients[item.id!]
            if (cl && cl.length > 0)
                item.clients = cl.map((client: string) => JSON.parse(client))
        })
        //console.log(apps)
        set(state => ({ loading: false, apps: apps }))
        return res.Code
    },
    async addApp(id: string, name: string, desc: string) {
        if (get().loading) return;

        set(state => ({ loading: true }))
        const res = await addApp({
            id: id,
            name: name,
            desc: desc
        })
        set(state => ({ loading: false }))
        return res
    },
    async removeApp(appid) {
        const code = await removeApp(appid)
        if (code === "200") {
            const apps = [...get().apps!]
            const index = apps.findIndex(item => item.id === appid)
            if (index! >= 0) {
                apps.splice(index!, 1)
                //console.log("DELETE",capp.clients)
                set(state => ({ apps: apps }))
            }
        }
        return code
    },
    async removeClient(app, clientid) {
        const code = await removeClient(clientid)
        // update data
        if (code === "200") {
            const apps = [...get().apps!]
            for (let index = 0; index < apps.length; index++) {
                const capp = apps[index];
                if (capp.id === app) {
                    const index = capp.clients?.findIndex(item => item.id === clientid)
                    if (index! < 0) break;

                    capp.clients!.splice(index!, 1)
                    //console.log("DELETE",capp.clients)
                    set(state => ({ apps: apps }))
                    break
                }
            }
        }
        return code
    },
    async updateApp(app: AppData) {
        const code = await updateApp(app)
        if (code === "200") {
            const apps = [...get().apps!]
            const index = apps?.findIndex((val) => val.id === app.id)
            apps[index] = app
            set(state => ({ apps: apps }))
        }
        return code
    },
    addClient(app, clientData) {
        return addClient(
            app,
            clientData.desc || "",
            clientData.redirect || "",
            (clientData.platform || "").toLowerCase(),
            clientData.domain || "",
            clientData.custom_hash || false,
            clientData.session_activity_timeout,
            clientData.session_ttl,
            clientData.allow_delete,
            clientData.allow_multiple_sessions,
            clientData.device_limit,
            clientData.login_retry_limit,
            clientData.new_session_on_reauth
        )
    },
    async updateClient(clientData) {
        const code = await updateClient(
            clientData.id,
            clientData.desc || "",
            clientData.redirect || "",
            (clientData.platform || "").toLowerCase(),
            clientData.domain || "",
            clientData.custom_hash || false,
            clientData.session_activity_timeout,
            clientData.session_ttl,
            clientData.allow_delete,
            clientData.allow_multiple_sessions,
            clientData.device_limit,
            clientData.login_retry_limit,
            clientData.new_session_on_reauth
        )
        if (code === "200") {
            // Update the client in the local state
            const apps = [...get().apps!]
            for (let appIndex = 0; appIndex < apps.length; appIndex++) {
                const app = apps[appIndex];
                if (app.clients) {
                    const clientIndex = app.clients.findIndex(client => client.id === clientData.id)
                    if (clientIndex >= 0) {
                        // Update the client data
                        apps[appIndex].clients![clientIndex] = {
                            ...apps[appIndex].clients![clientIndex],
                            ...clientData,
                            platform: (clientData.platform || "").toLowerCase()
                        }
                        set(state => ({ apps: apps }))
                        break
                    }
                }
            }
        }
        return code
    },
    async addRole(app, role) {
        const res = await addRole(app, role)
        if (res === "200") {
            const roles = [...get().currentRoles!]
            role.id = app + "." + role.id
            roles.push(role)
            set(state => ({ currentRoles: roles }))
        }
        return res
    },
    async retrieveRoles(app) {
        if (get().loadingRoles) return ""
        set(state => ({ loadingRoles: true }))
        const res = await retrieveRoles(app)
        if (res.Code === "200") {
            set(state => ({ currentRoles: res.Msg.roles }))
        }
        set(state => ({ loadingRoles: false }))
        return res.Code
    },
    async removeRole(role) {
        const code = await removeRole(role)
        if (code === "200") {
            const roles = [...get().currentRoles!]
            const index = roles.findIndex(item => item.id === role)
            if (index >= 0) {
                //console.log(index, roles)
                roles.splice(index, 1)
                set(state => ({ currentRoles: roles }))
            }
        }
        return code
    },
    async updateRole(role) {
        return ""
    },
    async uploadIcon(img, small, app) {
        const link = await uploadIcon(img, small, app)
        if (link.Code === "200") {
            const apps = [...get().apps!]
            const index = apps.findIndex(item => item.id === app)
            if (index >= 0) {
                if (small)
                    apps[index].icons = link.Msg
                else
                    apps[index].iconl = link.Msg
                set(state => ({ apps: apps }))
            }
        }
        return link.Msg
    }
}))